import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * Placeholder while the data agent builds a chart. The backend announces the
 * create_chart call (SSE `chart_pending`) the moment the model starts writing
 * it — the figures are still streaming in — and the real ChartCard replaces
 * this frame once the `chart` event arrives.
 *
 * Same frame as ChartCard (so nothing jumps on the swap): a shimmering title,
 * a faint grid and columns that keep growing and settling, plus a caption that
 * says what is happening.
 */

const BARS = [0.42, 0.58, 0.5, 0.72, 0.64, 0.86, 0.78];
const PLOT_HEIGHT = 200;

function ChartSkeleton() {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const faint = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.08);

  const shimmer = {
    position: 'relative',
    overflow: 'hidden',
    bgcolor: faint,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      transform: 'translateX(-100%)',
      background: `linear-gradient(90deg, transparent 0%, ${alpha(accent, 0.16)} 50%, transparent 100%)`,
      animation: 'chartShimmer 1.6s ease-in-out infinite',
    },
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        '@keyframes chartShimmer': { '100%': { transform: 'translateX(100%)' } },
        '@keyframes chartGrow': {
          '0%': { transform: 'scaleY(0.08)', opacity: 0.35 },
          '55%': { transform: 'scaleY(1)', opacity: 1 },
          '100%': { transform: 'scaleY(0.08)', opacity: 0.35 },
        },
        '@keyframes chartRise': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes chartDot': {
          '0%, 80%, 100%': { opacity: 0.25 },
          '40%': { opacity: 1 },
        },
        width: '100%',
        border: 1,
        borderColor: alpha(accent, 0.3),
        borderRadius: '10px',
        bgcolor: alpha(accent, 0.04),
        px: 2, pt: 1.5, pb: 1.25,
        my: 0.5,
        animation: 'chartRise 320ms ease-out both',
      }}
    >
      {/* title + subtitle placeholders */}
      <Box sx={{ ...shimmer, width: '46%', height: 14, borderRadius: '6px', mb: 0.75 }} />
      <Box sx={{ ...shimmer, width: '28%', height: 10, borderRadius: '6px', mb: 2 }} />

      {/* plot: faint grid + growing columns */}
      <Box sx={{ position: 'relative', height: PLOT_HEIGHT, display: 'flex', alignItems: 'flex-end', gap: '6%', px: '4%' }}>
        {[0.25, 0.5, 0.75].map((f) => (
          <Box key={f} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, height: '1px', bgcolor: faint }} />
        ))}
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: alpha(theme.palette.text.primary, 0.14) }} />
        {BARS.map((h, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              maxWidth: 28,
              height: `${h * 100}%`,
              borderRadius: '4px 4px 0 0',
              transformOrigin: 'bottom',
              background: `linear-gradient(180deg, ${alpha(accent, 0.55)} 0%, ${alpha(accent, 0.18)} 100%)`,
              animation: 'chartGrow 2.4s ease-in-out infinite',
              animationDelay: `${i * 140}ms`,
            }}
          />
        ))}
      </Box>

      {/* caption */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {t('chart_building', { defaultValue: 'Grafik wird erstellt' })}
        </Typography>
        <Box sx={{ display: 'flex', gap: '3px' }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{
              width: 4, height: 4, borderRadius: '50%', bgcolor: accent,
              animation: 'chartDot 1.2s ease-in-out infinite', animationDelay: `${i * 160}ms`,
            }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default ChartSkeleton;
